import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  MdCallMade, MdCallMissed, MdCallReceived, MdClose, MdErrorOutline,
  MdPause, MdPhone, MdPlayArrow, MdSubject,
} from 'react-icons/md';
import { cssRaw } from 'typestyle';
import { shortDuration } from './reportBits';

// Diallogs' AI analysis of one call, as views._insights shapes it.
export interface CallInsights {
  summary: string;
  action_points: string[];
  analysis: { label: string; value: string }[];
}
export interface RecentCall {
  shop: string; at: string; caller: string; number: string; name: string;
  type: string; duration: number; connected: boolean; recording_url: string;
  notes: string;
  id: string;
  insights: CallInsights | null;
  transcript: string;
}

// The one analysis row worth pulling out on its own: what to do next.
const NEXT_STEP = 'Suggested next step';
const SPEEDS = [1, 1.25, 1.5, 2];

/** m:ss for the player's clock. */
const clock = (s: number) => {
  if (!isFinite(s) || s < 0) { return '0:00'; }
  const t = Math.floor(s);
  return `${Math.floor(t / 60)}:${t % 60 < 10 ? '0' : ''}${t % 60}`;
};

// Diallogs usually writes its summary as one "Topic: what was said" line per
// topic -- but not always; some calls get plain sentences. Each line is shown
// on its own, with the topic set apart when there is one.
const TOPIC = /^([^:"“]{2,60}):\s+(\S[\s\S]*)$/;
const summaryLines = (text: string) => (text || '').split(/\n+/)
  .map((l) => l.trim())
  .filter(Boolean)
  .map((l) => {
    const m = TOPIC.exec(l);
    return m && m[1].split(/\s+/).length <= 9
      ? { topic: m[1], text: m[2] }
      : { topic: '', text: l };
  });

const isMissed = (type: string) => /miss|reject|block/i.test(type || '');
const typeIcon = (type: string) => {
  if (isMissed(type)) { return <MdCallMissed />; }
  if (/out/i.test(type || '')) { return <MdCallMade />; }
  if (/in/i.test(type || '')) { return <MdCallReceived />; }
  return <MdPhone />;
};

cssRaw(`
  /* A quiet layer over the page: lighter than a blackout, so the list stays
     recognisable behind it. Above the sticky top bar (z-index 60) and
     sha-el's own modal (1000/1001). */
  .cs-cd__shade {
    position: fixed; top: 0; right: 0; bottom: 0; left: 0; z-index: 1100;
    display: flex; align-items: flex-start; justify-content: center;
    padding: 8vh 16px 16px;
    background: rgba(15,37,71,0.28);
    -webkit-backdrop-filter: blur(2px); backdrop-filter: blur(2px);
    animation: cs-cd-fade .16s ease-out;
  }
  /* max-height against the shade, not the viewport: on phones vh is the
     height with the browser's toolbar hidden, so a vh cap overflows the
     visible screen while the toolbar shows. The shade follows the visible
     screen, and its padding keeps the margins. */
  .cs-cd {
    display: flex; flex-direction: column;
    width: 100%; max-width: 660px; max-height: 100%;
    background: #fff; border-radius: 16px; overflow: hidden;
    border: 1px solid rgba(15,37,71,0.06);
    box-shadow: 0 18px 48px rgba(15,37,71,0.16), 0 2px 6px rgba(15,37,71,0.05);
    animation: cs-cd-rise .2s cubic-bezier(.2,.8,.2,1);
  }
  .cs-cd:focus { outline: none; }

  .cs-cd__top {
    flex: none; padding: 18px 20px 16px;
    border-bottom: 1px solid rgba(15,37,71,0.06);
  }
  .cs-cd__head { display: flex; align-items: flex-start; gap: 12px; }
  .cs-cd__avatar {
    flex: none; display: inline-flex; align-items: center; justify-content: center;
    width: 40px; height: 40px; border-radius: 12px;
    background: #EEF3FC; color: #1E40AF; font-size: 20px;
  }
  .cs-cd__avatar--missed { background: #FCF0F0; color: #B4232A; }
  .cs-cd__title { flex: 1 1 auto; min-width: 0; padding-top: 1px; }
  .cs-cd__title h3 {
    margin: 0; font-size: 16px; font-weight: 650; line-height: 1.3;
    color: #0F2547; word-break: break-word;
  }
  .cs-cd__meta {
    display: flex; flex-wrap: wrap; margin-top: 3px;
    font-size: 12.5px; line-height: 1.5; color: #7C93B5;
  }
  /* The dot trails each item, so a wrapped line never starts with one. */
  .cs-cd__meta > span:not(:last-child)::after { content: '\\00B7'; margin: 0 6px; color: #B8C4D8; }
  .cs-cd__num { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 12px; }
  .cs-cd__close {
    flex: none; display: inline-flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; margin: -4px -6px 0 0; padding: 0; cursor: pointer;
    border: 0; border-radius: 50%; background: transparent;
    color: #8A9BB8; font-size: 20px;
    transition: background-color .15s ease, color .15s ease;
  }
  .cs-cd__close:hover { background: rgba(15,37,71,0.06); color: #0F2547; }

  /* The player: one soft strip in the app's own colours, instead of each
     browser's own grey control. */
  .cs-cd__player {
    display: flex; align-items: center; gap: 10px;
    margin-top: 14px; padding: 7px 12px 7px 7px;
    border-radius: 14px; background: #F4F7FC;
  }
  .cs-cd__play {
    position: relative; flex: none;
    display: inline-flex; align-items: center; justify-content: center;
    width: 38px; height: 38px; padding: 0; cursor: pointer;
    border: 0; border-radius: 50%; color: #fff; font-size: 22px;
    background: linear-gradient(135deg, #0A6BA8 0%, #1E40AF 100%);
    box-shadow: 0 3px 10px rgba(30,64,175,0.22);
    transition: transform .15s ease, box-shadow .15s ease;
  }
  .cs-cd__play:hover { transform: scale(1.05); box-shadow: 0 4px 14px rgba(30,64,175,0.3); }
  .cs-cd__play--wait::after {
    content: ''; position: absolute; top: -4px; right: -4px; bottom: -4px; left: -4px;
    border-radius: 50%; border: 2px solid transparent; border-top-color: rgba(30,64,175,0.55);
    animation: cs-cd-spin .8s linear infinite;
  }
  .cs-cd__time {
    flex: none; min-width: 32px; font-size: 12px; color: #52658A;
    font-variant-numeric: tabular-nums;
  }
  .cs-cd__time--end { text-align: right; color: #8A9BB8; }
  .cs-cd__seek {
    -webkit-appearance: none; appearance: none;
    flex: 1 1 auto; min-width: 60px; height: 18px; margin: 0; padding: 0;
    background: transparent; cursor: pointer;
  }
  .cs-cd__seek:focus { outline: none; }
  .cs-cd__seek:disabled { cursor: default; opacity: .55; }
  .cs-cd__seek::-webkit-slider-runnable-track {
    height: 4px; border-radius: 999px;
    background: linear-gradient(to right, #1E40AF 0, #1E40AF var(--pct, 0%),
      rgba(15,37,71,0.13) var(--pct, 0%));
  }
  .cs-cd__seek::-webkit-slider-thumb {
    -webkit-appearance: none; width: 12px; height: 12px; margin-top: -4px;
    border-radius: 50%; background: #fff; border: 2px solid #1E40AF;
    box-shadow: 0 1px 3px rgba(15,37,71,0.2);
  }
  .cs-cd__seek::-moz-range-track { height: 4px; border-radius: 999px; background: rgba(15,37,71,0.13); }
  .cs-cd__seek::-moz-range-progress { height: 4px; border-radius: 999px; background: #1E40AF; }
  .cs-cd__seek::-moz-range-thumb {
    width: 9px; height: 9px; border-radius: 50%; background: #fff; border: 2px solid #1E40AF;
  }
  .cs-cd__seek:focus-visible::-webkit-slider-thumb { box-shadow: 0 0 0 4px rgba(37,99,235,0.22); }
  /* Its own rule: a browser drops a whole selector list that names a
     pseudo-element it does not know, so this cannot share the one above. */
  .cs-cd__seek:focus-visible::-moz-range-thumb { box-shadow: 0 0 0 4px rgba(37,99,235,0.22); }
  .cs-cd__speed {
    flex: none; min-width: 44px; padding: 4px 8px; cursor: pointer;
    border: 1px solid rgba(15,37,71,0.1); border-radius: 999px; background: #fff;
    font: inherit; font-size: 12px; font-weight: 650; color: #45587C;
    font-variant-numeric: tabular-nums;
  }
  .cs-cd__speed:hover { color: #1E40AF; border-color: rgba(30,64,175,0.3); }
  .cs-cd__player--note { padding: 11px 14px; font-size: 13px; color: #7C93B5; }
  .cs-cd__player--bad {
    flex-wrap: wrap; padding: 9px 10px 9px 14px;
    background: #FCF1F1; color: #A61E25; font-size: 13px;
  }
  .cs-cd__player--bad svg { flex: none; font-size: 18px; }
  .cs-cd__player--bad span { flex: 1 1 auto; }
  .cs-cd__retry {
    flex: none; padding: 5px 12px; cursor: pointer;
    border: 1px solid rgba(166,30,37,0.25); border-radius: 999px; background: #fff;
    font: inherit; font-size: 12px; font-weight: 650; color: #A61E25;
  }
  .cs-cd__retry:hover { background: #A61E25; color: #fff; border-color: transparent; }
  .cs-cd__play:focus-visible, .cs-cd__speed:focus-visible,
  .cs-cd__close:focus-visible, .cs-cd__retry:focus-visible {
    outline: 2px solid #2563EB; outline-offset: 2px;
  }

  /* What Diallogs' AI made of the call, scrolling beneath the player. */
  .cs-cd__body {
    flex: 1 1 auto; min-height: 0; overflow-y: auto;
    padding: 4px 20px 20px;
    -webkit-overflow-scrolling: touch;
  }
  /* A thin, quiet scrollbar: the app-wide one is heavy inside a small box. */
  .cs-cd__body { scrollbar-width: thin; scrollbar-color: rgba(15,37,71,0.2) transparent; }
  .cs-cd__body::-webkit-scrollbar { width: 6px; background: transparent; }
  .cs-cd__body::-webkit-scrollbar-track { background: transparent; -webkit-box-shadow: none; box-shadow: none; }
  .cs-cd__body::-webkit-scrollbar-thumb { background: rgba(15,37,71,0.16); border-radius: 3px; }
  .cs-cd__body:focus { outline: none; }
  .cs-cd__body:focus-visible { box-shadow: inset 0 0 0 2px rgba(37,99,235,0.16); }
  .cs-cd__h {
    margin: 18px 0 8px; font-size: 11px; font-weight: 700;
    letter-spacing: .06em; text-transform: uppercase; color: #8A9BB8;
  }
  .cs-cd__lines { list-style: none; margin: 0; padding: 0; }
  .cs-cd__lines li {
    padding: 8px 0; font-size: 13.5px; line-height: 1.55; color: #22324F;
    border-top: 1px solid rgba(15,37,71,0.06);
  }
  .cs-cd__lines li:first-child { border-top: 0; padding-top: 2px; }
  .cs-cd__topic { display: block; margin-bottom: 1px; font-size: 12px; font-weight: 600; color: #6B7FA3; }
  .cs-cd__next {
    margin: 14px 0 0; padding: 11px 14px; border-radius: 12px;
    background: #F0F5FF; border: 1px solid rgba(30,64,175,0.1);
    font-size: 13.5px; line-height: 1.5; color: #1B2F57;
  }
  .cs-cd__next b {
    display: block; margin-bottom: 2px;
    font-size: 11.5px; font-weight: 650; color: #1E40AF;
  }
  .cs-cd__points { list-style: none; margin: 0; padding: 0; }
  .cs-cd__points li {
    position: relative; padding: 3px 0 3px 16px;
    font-size: 13.5px; line-height: 1.5; color: #22324F;
  }
  .cs-cd__points li::before {
    content: ''; position: absolute; left: 2px; top: 11px;
    width: 6px; height: 6px; border-radius: 50%; background: #7AA0E0;
  }
  .cs-cd__tiles {
    display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px; margin: 0;
  }
  .cs-cd__tile {
    padding: 9px 12px; border-radius: 10px;
    background: #F7F9FC; border: 1px solid rgba(15,37,71,0.05);
  }
  .cs-cd__tile dt { margin-bottom: 2px; font-size: 11.5px; font-weight: 600; color: #7C93B5; }
  .cs-cd__tile dd { margin: 0; font-size: 13px; line-height: 1.45; color: #22324F; }
  .cs-cd__text { margin: 0; font-size: 13.5px; line-height: 1.55; color: #22324F; white-space: pre-line; }
  .cs-cd__foot { margin: 18px 0 0; font-size: 11.5px; line-height: 1.5; color: #9AA9C2; }
  .cs-cd__empty {
    display: flex; flex-direction: column; align-items: center; text-align: center;
    padding: 26px 12px 10px; color: #7C93B5; font-size: 13px; line-height: 1.5;
  }
  .cs-cd__empty svg { font-size: 26px; color: #B8C4D8; margin-bottom: 6px; }
  .cs-cd__empty b { color: #45587C; font-weight: 650; margin-bottom: 2px; }

  @keyframes cs-cd-fade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes cs-cd-rise {
    from { opacity: 0; transform: translateY(8px) scale(.985); }
    to { opacity: 1; transform: none; }
  }
  @keyframes cs-cd-sheet { from { transform: translateY(28px); opacity: .6; } to { transform: none; opacity: 1; } }
  @keyframes cs-cd-spin { to { transform: rotate(360deg); } }

  /* Phones: a sheet rising from the bottom edge, within thumb reach. */
  @media (max-width: 620px) {
    .cs-cd__shade { padding: 40px 0 0; align-items: flex-end; }
    .cs-cd {
      max-width: none; border-radius: 18px 18px 0 0; border-bottom: 0;
      animation-name: cs-cd-sheet;
    }
    .cs-cd__top { padding: 16px 16px 14px; }
    .cs-cd__body { padding: 4px 16px 16px; padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px)); }
  }
  @media (max-width: 480px) {
    .cs-cd__tiles { grid-template-columns: 1fr; }
  }
  @media (prefers-reduced-motion: reduce) {
    .cs-cd__shade, .cs-cd { animation: none; }
    .cs-cd__play--wait::after { animation-duration: 2s; }
  }
`);

interface CallDialogProps {
  call: RecentCall;
  // Opened from the play button: start the recording straight away. Opened
  // from Summary: the player is there, but waits to be asked.
  autoPlay: boolean;
  onClose: () => void;
}
interface CallDialogState {
  failed: boolean;
  // Bumped by "Try again" so React mounts a fresh <audio>, which re-fetches.
  attempt: number;
  playing: boolean;
  // Asked to play, but the sound has not started flowing yet.
  waiting: boolean;
  position: number;
  // The position as a screen reader should hear it: moved only when the
  // user acts, so the time is not read out every second over the call.
  spoken: number;
  // The recording's own length, once the browser has read it.
  length: number;
  speed: number;
}

/**
 * One call in a modal: its recording and Diallogs' AI summary together, so
 * the summary can be read while the call plays.
 *
 * Rendered into <body> through a portal. sha-el animates its containers with
 * transforms, and a position:fixed box inside a transformed parent is fixed
 * to that parent rather than to the window.
 *
 * Diallogs sends no word-for-word transcript (its API has no field for one)
 * and the owner decided the summary is enough, so the transcript section
 * appears only if that column is ever filled.
 */
export class CallDialog extends React.Component<CallDialogProps, CallDialogState> {
  state: CallDialogState = {
    failed: false, attempt: 0, playing: false, waiting: false,
    position: 0, spoken: 0, length: 0, speed: 1,
  };
  private box = React.createRef<HTMLDivElement>();
  private audio = React.createRef<HTMLAudioElement>();
  private player = React.createRef<HTMLDivElement>();
  private playButton = React.createRef<HTMLButtonElement>();
  private reading = React.createRef<HTMLDivElement>();
  private retryButton = React.createRef<HTMLButtonElement>();
  // The player had focus when it failed: the element holding it is about to
  // be replaced, so focus goes to "Try again" instead of falling out of the
  // dialog onto the page.
  private refocusRetry = false;
  // Where focus was before the dialog took it, handed back on close.
  private returnFocus: HTMLElement = null;
  private bodyStyle = { overflow: '', paddingRight: '' };
  // A press that starts inside the dialog and is released on the shade --
  // selecting summary text, say -- must not count as a click outside.
  private pressedShade = false;

  componentDidMount() {
    this.returnFocus = document.activeElement as HTMLElement;
    // Stop the page behind from scrolling under the dialog, without the
    // layout jumping sideways as its scrollbar goes.
    const body = document.body;
    this.bodyStyle = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
    const gutter = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (gutter > 0) { body.style.paddingRight = `${gutter}px`; }
    document.addEventListener('keydown', this.onKeyDown);
    // Focus starts on the summary, the one part that scrolls, so the arrow
    // keys and Page Down read on at once. Tab reaches Close and the player.
    const first = this.reading.current || this.box.current;
    if (first) { first.focus(); }
    // Still inside the click that opened the dialog -- React mounts it before
    // that event returns -- so browsers that let only a click start sound
    // (Safari) allow this.
    if (this.props.autoPlay) { this.start(); }
  }

  componentDidUpdate(prevProps: CallDialogProps, prevState: CallDialogState) {
    if (!prevState.failed && this.state.failed && this.refocusRetry) {
      this.refocusRetry = false;
      if (this.retryButton.current) { this.retryButton.current.focus(); }
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown);
    const body = document.body;
    body.style.overflow = this.bodyStyle.overflow;
    body.style.paddingRight = this.bodyStyle.paddingRight;
    // Taking the element out of the page pauses it anyway; saying so costs
    // nothing and does not depend on it.
    if (this.audio.current) { this.audio.current.pause(); }
    const back = this.returnFocus;
    if (back && back.focus && document.body.contains(back)) { back.focus(); }
  }

  start = () => {
    const a = this.audio.current;
    if (!a) { return; }
    a.playbackRate = this.state.speed;
    const p: any = a.play();
    // Refused by the browser's autoplay rules, or cut short by a close: the
    // play button still works, so there is nothing to report.
    if (p && p.catch) {
      p.catch(() => {
        if (this.audio.current === a) { this.setState({ playing: false, waiting: false }); }
      });
    }
  }

  togglePlay = () => {
    const a = this.audio.current;
    if (!a) { return; }
    if (a.paused) { this.start(); } else { a.pause(); }
  }

  // The bar counts tenths of a second, with no step attribute: dragging stays
  // smooth, and VoiceOver's adjust gesture -- which uses the step when one is
  // set -- moves 5% of the recording instead of a tenth of a second.
  seek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const a = this.audio.current;
    const to = parseFloat(e.target.value) / 10;
    if (!a || !isFinite(to)) { return; }
    a.currentTime = to;
    this.setState({ position: to, spoken: to });
  }

  // Arrow keys on the bar jump five seconds; a tenth of a second per press
  // would be useless.
  seekKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const jump = e.key === 'ArrowRight' || e.key === 'ArrowUp' ? 5
      : e.key === 'ArrowLeft' || e.key === 'ArrowDown' ? -5 : 0;
    const a = this.audio.current;
    if (!jump || !a) { return; }
    e.preventDefault();
    const total = this.total();
    const to = Math.max(0, Math.min(total || a.currentTime + jump, a.currentTime + jump));
    a.currentTime = to;
    this.setState({ position: to, spoken: to });
  }

  cycleSpeed = () => {
    const i = SPEEDS.indexOf(this.state.speed);
    const speed = SPEEDS[(i + 1) % SPEEDS.length];
    if (this.audio.current) { this.audio.current.playbackRate = speed; }
    this.setState({ speed });
  }

  /** The recording's length: its own once read, else the call's. */
  total = () => this.state.length || this.props.call.duration || 0;

  onMeta = () => {
    const a = this.audio.current;
    if (!a) { return; }
    // A fresh element after "Try again" starts at 1x; keep the chosen speed.
    a.playbackRate = this.state.speed;
    if (isFinite(a.duration) && a.duration > 0) { this.setState({ length: a.duration }); }
  }

  onTime = () => {
    const a = this.audio.current;
    if (a) { this.setState({ position: a.currentTime }); }
  }

  onStop = () => {
    const a = this.audio.current;
    this.setState({
      playing: false, waiting: false,
      spoken: a ? a.currentTime : this.state.position,
    });
  }

  // Arriving on the bar, a screen reader should hear where playback is now.
  onSeekFocus = () => this.setState({ spoken: this.state.position });

  onAudioError = () => {
    const holder = this.player.current;
    this.refocusRetry = !!holder && holder.contains(document.activeElement);
    this.setState({ failed: true, playing: false, waiting: false });
  }

  // "Try again" is replaced by the new player, so focus moves onto its play
  // button rather than being dropped with the old button.
  retry = () => this.setState(
    (s) => ({
      failed: false, attempt: s.attempt + 1, playing: false, waiting: false,
      position: 0, spoken: 0, length: 0,
    }),
    () => {
      if (this.playButton.current) { this.playButton.current.focus(); }
      this.start();
    },
  )

  onKeyDown = (e: KeyboardEvent) => {
    if (e.defaultPrevented) { return; }
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      this.props.onClose();
    }
  }

  focusables = (): HTMLElement[] => {
    const box = this.box.current;
    if (!box) { return []; }
    return Array.prototype.slice.call(box.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), [href], [tabindex="0"]'));
  }

  // Tab is kept inside the dialog by a focusable guard either side of it:
  // reaching one sends focus round to the other end.
  wrapToFirst = () => {
    const items = this.focusables();
    if (items.length) { items[0].focus(); } else if (this.box.current) { this.box.current.focus(); }
  }

  wrapToLast = () => {
    const items = this.focusables();
    if (items.length) {
      items[items.length - 1].focus();
    } else if (this.box.current) { this.box.current.focus(); }
  }

  onShadeDown = (e: React.MouseEvent<HTMLDivElement>) => {
    this.pressedShade = e.target === e.currentTarget;
  }

  onShadeClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // detail > 1 is the second click of a double-click on the play button,
    // landing on the shade that the first click just opened.
    if (this.pressedShade && e.target === e.currentTarget && e.nativeEvent.detail < 2) {
      this.props.onClose();
    }
  }

  renderPlayer() {
    const c = this.props.call;
    if (!c.recording_url) {
      return (
        <div className='cs-cd__player cs-cd__player--note'>
          No recording for this call.
        </div>
      );
    }
    if (this.state.failed) {
      return (
        <div className='cs-cd__player cs-cd__player--bad' role='alert' ref={this.player}>
          <MdErrorOutline />
          <span>This recording could not be loaded.</span>
          <button
            type='button'
            className='cs-cd__retry'
            onClick={this.retry}
            ref={this.retryButton}
          >
            Try again
          </button>
        </div>
      );
    }
    const { playing, waiting, position, spoken, speed } = this.state;
    const total = this.total();
    const at = Math.min(position, total || position);
    const heard = Math.min(spoken, total || spoken);
    const pct = total ? Math.min(100, (at / total) * 100) : 0;
    // React 16 passes CSS custom properties through style; the typings do not
    // list them.
    const fill: any = { '--pct': `${pct}%` };
    return (
      <div className='cs-cd__player' ref={this.player}>
        <audio
          key={this.state.attempt}
          ref={this.audio}
          src={c.recording_url}
          preload='metadata'
          onLoadedMetadata={this.onMeta}
          onDurationChange={this.onMeta}
          onTimeUpdate={this.onTime}
          onPlay={() => this.setState({ playing: true, waiting: true })}
          onPlaying={() => this.setState({ waiting: false })}
          onWaiting={() => this.setState({ waiting: true })}
          onPause={this.onStop}
          onEnded={this.onStop}
          onError={this.onAudioError}
        />
        <button
          type='button'
          className={`cs-cd__play${playing && waiting ? ' cs-cd__play--wait' : ''}`}
          onClick={this.togglePlay}
          aria-label={playing ? 'Pause' : 'Play'}
          title={playing ? 'Pause' : 'Play'}
          ref={this.playButton}
        >
          {playing ? <MdPause /> : <MdPlayArrow />}
        </button>
        <span className='cs-cd__time'>{clock(at)}</span>
        <input
          type='range'
          className='cs-cd__seek'
          min={0}
          max={Math.round(total * 10)}
          value={Math.round(at * 10)}
          onChange={this.seek}
          onKeyDown={this.seekKey}
          onFocus={this.onSeekFocus}
          disabled={!total}
          style={fill}
          aria-label='Position in the recording'
          aria-valuetext={`${clock(heard)} of ${clock(total)}`}
        />
        <span className='cs-cd__time cs-cd__time--end'>{total ? clock(total) : '--:--'}</span>
        <button
          type='button'
          className='cs-cd__speed'
          onClick={this.cycleSpeed}
          aria-label={`Playback speed ${speed}x`}
          title='Playback speed'
        >
          {speed}&times;
        </button>
      </div>
    );
  }

  renderSummary() {
    const c = this.props.call;
    const ins = c.insights;
    const transcript = c.transcript ? (
      <>
        <h4 className='cs-cd__h'>Transcript</h4>
        <p className='cs-cd__text'>{c.transcript}</p>
      </>
    ) : null;
    if (!ins) {
      return (
        <>
          <div className='cs-cd__empty'>
            <MdSubject />
            <b>No AI summary for this call</b>
            <span>
              {c.recording_url
                ? 'Diallogs writes one only for recorded calls, and only since 19 Sep 2026.'
                : 'Diallogs writes one from the recording, and this call has none.'}
            </span>
          </div>
          {transcript}
        </>
      );
    }
    const lines = summaryLines(ins.summary);
    const next = ins.analysis.filter((a) => a.label === NEXT_STEP)[0];
    const rest = ins.analysis.filter((a) => a !== next);
    return (
      <>
        {lines.length ? (
          <>
            <h4 className='cs-cd__h'>Summary</h4>
            <ul className='cs-cd__lines'>
              {lines.map((l, i) => (
                <li key={i}>
                  {l.topic ? <span className='cs-cd__topic'>{l.topic}</span> : null}
                  {l.text}
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {next ? (
          <div className='cs-cd__next'>
            <b>Suggested next step</b>
            {next.value}
          </div>
        ) : null}

        {ins.action_points.length ? (
          <>
            <h4 className='cs-cd__h'>Action points</h4>
            <ul className='cs-cd__points'>
              {ins.action_points.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </>
        ) : null}

        {rest.length ? (
          <>
            <h4 className='cs-cd__h'>How the call went</h4>
            <dl className='cs-cd__tiles'>
              {rest.map((a, i) => (
                <div className='cs-cd__tile' key={i}>
                  <dt>{a.label}</dt>
                  <dd>{a.value}</dd>
                </div>
              ))}
            </dl>
          </>
        ) : null}

        {transcript}
        <p className='cs-cd__foot'>
          Written by Diallogs&apos; AI from the recording. Quotes are its English
          rendering; listen to hear the call word for word.
        </p>
      </>
    );
  }

  render() {
    const c = this.props.call;
    const title = c.shop || c.name || c.number || 'Call';
    return createPortal(
      <div
        className='cs-cd__shade'
        onMouseDown={this.onShadeDown}
        onClick={this.onShadeClick}
      >
        <div tabIndex={0} onFocus={this.wrapToLast} />
        <div
          className='cs-cd'
          role='dialog'
          aria-modal={true}
          aria-labelledby='cs-cd-title'
          tabIndex={-1}
          ref={this.box}
        >
          <div className='cs-cd__top'>
            <div className='cs-cd__head'>
              <span
                className={`cs-cd__avatar${isMissed(c.type) ? ' cs-cd__avatar--missed' : ''}`}
                aria-hidden={true}
              >
                {typeIcon(c.type)}
              </span>
              <div className='cs-cd__title'>
                <h3 id='cs-cd-title'>{title}</h3>
                <div className='cs-cd__meta'>
                  {c.number && title !== c.number
                    ? <span className='cs-cd__num'>{c.number}</span>
                    : null}
                  {c.type ? <span>{c.type}</span> : null}
                  <span>{c.at}</span>
                  <span>{c.caller}</span>
                  {c.duration ? <span>{shortDuration(c.duration)}</span> : null}
                </div>
              </div>
              <button
                type='button'
                className='cs-cd__close'
                onClick={this.props.onClose}
                aria-label='Close'
                title='Close (Esc)'
              >
                <MdClose />
              </button>
            </div>
            {this.renderPlayer()}
          </div>
          {/* A Tab stop of its own: Safari never lets the keyboard into a
              scrolling box otherwise, and a long summary would be cut off. */}
          <div
            className='cs-cd__body'
            tabIndex={0}
            role='region'
            aria-label='AI summary'
            ref={this.reading}
          >
            {this.renderSummary()}
          </div>
        </div>
        <div tabIndex={0} onFocus={this.wrapToFirst} />
      </div>,
      document.body,
    );
  }
}
